import { useTranslation } from "react-i18next";
import {
	ChartCard,
	type Column,
	ColumnChart,
	HBarList,
	Histogram,
	holatVar,
} from "../../../../../shared/charts";
import type { AnalyticsLayoutProps } from "../../../types";
import { KpiRow, sonMatni } from "../../detail/KpiRow";
import { AnalyticsGrid, kunYorlig, sanaQisqa, soatDaqiqa, useBolimNomi } from "./shared";

/**
 * `work_time` analitik ko'rinishi (reja F1):
 * KPI (daqiqalar `H:MM`) → [kunlar ustunlari | ish soatlari gistogrammasi] →
 * [hududdan tashqarida eng ko'p xodimlar | bo'limlar o'rtachasi].
 *
 * Konvert (`workTime.ts`): `daily.primary` — ishlangan DAQIQA
 * (`flagged` — kunlar SONI, boshqa birlik — ustunga qo'shilmaydi);
 * `topEmployees.total` — tashqaridagi daqiqa;
 * ⚠️ `byDepartment.total` — SON EMAS, bir xodim-kunga O'RTACHA
 * ishlangan daqiqa.
 */
export function WorkTimeAnalytics({ kind, data }: AnalyticsLayoutProps) {
	const { t } = useTranslation("reports");
	const bolimNomi = useBolimNomi();
	const bosh = t("analytics.attendance.empty");
	const ishlanganYorliq = t("analytics.attendance.workTime.worked");

	const ustunlar: Column[] = data.daily.map((kun) => ({
		key: kun.date,
		label: kunYorlig(kun.date),
		stack: [{ value: kun.primary, color: holatVar("kelgan"), label: ishlanganYorliq }],
		tooltip: `${sanaQisqa(kun.date)}: ${soatDaqiqa(kun.primary)}`,
	}));

	const binlar = data.histogram.map((b) => ({ label: b.bucket, count: b.count }));

	const xodimlar = data.topEmployees.map((x) => ({
		key: x.employeeId,
		label: x.employeeName,
		value: x.total,
		valueText: soatDaqiqa(x.total),
		color: holatVar("ertaKetgan"),
	}));

	const bolimlar = data.byDepartment.map((b) => ({
		key: b.departmentId ?? "none",
		label: bolimNomi(b.departmentName),
		value: b.total,
		valueText: soatDaqiqa(b.total),
	}));

	return (
		<div data-analytics-layout="work_time" className="flex flex-col gap-4">
			<KpiRow
				kind={kind}
				totals={data.totals}
				spark={data.spark}
				items={[
					{
						key: "workedMin",
						label: t("analytics.attendance.workTime.kpi.workedMin"),
						format: soatDaqiqa,
					},
					{
						key: "insideMin",
						label: t("analytics.attendance.workTime.kpi.insideMin"),
						format: soatDaqiqa,
					},
					{
						key: "outsideMin",
						label: t("analytics.attendance.workTime.kpi.outsideMin"),
						format: soatDaqiqa,
						danger: true,
					},
					{
						key: "employees",
						label: t("analytics.attendance.workTime.kpi.employees"),
						format: sonMatni,
					},
				]}
			/>

			<AnalyticsGrid cols={2}>
				<ChartCard
					title={t("analytics.attendance.workTime.columnsTitle")}
					subtitle={t("analytics.attendance.workTime.columnsSubtitle")}
				>
					<div data-daily-columns>
						<ColumnChart columns={ustunlar} empty={bosh} />
					</div>
				</ChartCard>

				<ChartCard
					title={t("analytics.attendance.workTime.histogramTitle")}
					subtitle={t("analytics.attendance.workTime.histogramSubtitle")}
				>
					<Histogram bins={binlar} empty={bosh} />
				</ChartCard>
			</AnalyticsGrid>

			<AnalyticsGrid cols={2}>
				<ChartCard
					title={t("analytics.attendance.workTime.topTitle")}
					subtitle={t("analytics.attendance.workTime.topSubtitle")}
				>
					<HBarList rows={xodimlar} empty={bosh} />
				</ChartCard>

				<ChartCard
					title={t("analytics.attendance.workTime.departmentsTitle")}
					subtitle={t("analytics.attendance.workTime.departmentsSubtitle")}
				>
					<HBarList rows={bolimlar} empty={bosh} />
				</ChartCard>
			</AnalyticsGrid>
		</div>
	);
}
